import { DebugService } from '../debug/debug.service.js';
import { CODELESS_ENTRY_CONST } from '../../constants/constants.js';

/**
 * Service for validating and parsing returnCust parameter format
 * New format patterns:
 *
 * Codeless Entry: COAF_REFI_CE_{Version}_{Allocation}
 * Examples:
 * - COAF_REFI_CE_V1_grlpv1
 *
 * Express Return: COAF_REFI_ER_{Version}_{AppId}_{TemplateId}_{ApplicantType}[_{ProfileType}]
 * Examples:
 * - COAF_REFI_ER_V1_9876543_42012_PRIMARY_FA
 * - COAF_REFI_ER_V1_9876543_42012_PRIMARY_NFA
 * - COAF_REFI_ER_V1_9876543_42012_PRIMARY_NA
 * - COAF_REFI_ER_V1_9876543_42012_SECONDARY  (no profileType for secondary)
 */
class _ReturnCustValidationService {
  constructor() {
    this.debug = new DebugService('ReturnCustValidationService');
  }

  /**
   * Validates and parses the returnCust parameter
   * @param {string} returnCust - The returnCust parameter value
   * @returns {Object|null} - Parsed and validated components or null if invalid
   */
  validateAndParseReturnCust(returnCust) {
    if (!returnCust || typeof returnCust !== 'string') {
      this.debug.log('validateAndParseReturnCust: invalid input type', returnCust);
      return null;
    }

    // Try to parse as Codeless Entry format: COAF_REFI_CE_{Version}_{Allocation}
    const codelessResult = this._parseCodelessEntry(returnCust);
    if (codelessResult) {
      this.debug.log('validateAndParseReturnCust: successfully parsed as codeless entry', codelessResult);
      return codelessResult;
    }

    // Try to parse as Express Return format: COAF_REFI_ER_{Version}_{AppId}_{TemplateId}
    const expressResult = this._parseExpressReturn(returnCust);
    if (expressResult) {
      this.debug.log('validateAndParseReturnCust: successfully parsed as express return', expressResult);
      return expressResult;
    }

    this.debug.log('validateAndParseReturnCust: no valid format matched', returnCust);
    return null;
  }

  /**
   * Diagnoses why a value failed to parse as Codeless Entry format, for callers that
   * only care about the codeless-entry path (e.g. the refi_allocation guard) and want
   * a specific reason to report in analytics, without affecting the shared parser's
   * behavior for express-return callers.
   * @param {string} returnCust - The returnCust/refi_allocation parameter value
   * @returns {string} - One of: 'invalid_input_type', 'wrong_part_count', 'invalid_prefix', 'invalid_version_format', 'missing_allocation'
   */
  getCodelessEntryFailureReason(returnCust) {
    if (!returnCust || typeof returnCust !== 'string') {
      return 'invalid_input_type';
    }

    const parts = returnCust.split('_');
    if (parts.length !== 5) {
      return 'wrong_part_count';
    }

    const [prefix1, prefix2, prefix3, version, allocation] = parts;

    if (prefix1 !== 'COAF' || prefix2 !== 'REFI' || prefix3 !== 'CE') {
      return 'invalid_prefix';
    }

    if (!version || !version.match(/^V\d+$/i)) {
      return 'invalid_version_format';
    }

    if (!allocation) {
      return 'missing_allocation';
    }

    return 'unknown';
  }

  /**
   * Parses Codeless Entry format: COAF_REFI_CE_{Version}_{Allocation}
   * @param {string} returnCust - The returnCust parameter value
   * @returns {Object|null} - Parsed codeless entry data or null if invalid
   */
  _parseCodelessEntry(returnCust) {
    const parts = returnCust.split('_');

    // Must have exactly 5 parts: COAF_REFI_CE_{Version}_{Allocation}
    if (parts.length !== 5) {
      this.debug.log('_parseCodelessEntry: unexpected number of parts', returnCust);
      return null;
    }

    const [prefix1, prefix2, prefix3, version, allocation] = parts;

    // Validate prefix parts
    if (prefix1 !== 'COAF' || prefix2 !== 'REFI' || prefix3 !== 'CE') {
      this.debug.log('_parseCodelessEntry: invalid prefix', returnCust);
      return null;
    }

    // Validate version format (V1, V2, etc.)
    if (!version || !version.match(/^V\d+$/i)) {
      this.debug.log('_parseCodelessEntry: invalid version format', returnCust);
      return null;
    }

    // Validate allocation exists
    if (!allocation) {
      this.debug.log('_parseCodelessEntry: missing allocation', returnCust);
      return null;
    }

    return {
      type: 'codeless-entry',
      version,
      allocation,
      isValidFormat: true,
      isCodelessEntryControl: allocation.toLowerCase() === CODELESS_ENTRY_CONST.ALLOCATION.CONTROL,
      isCodelessEntryV1: allocation.toLowerCase() === CODELESS_ENTRY_CONST.ALLOCATION.VARIANT_1,
      isExpressReturn: false,
    };
  }

  /**
   * Parses Express Return format: COAF_REFI_ER_{Version}_{AppId}_{TemplateId}_{ApplicantType}[_{ProfileType}]
   * ProfileType (FA/NFA/NA) is present for PRIMARY applicants, absent for SECONDARY.
   * @param {string} returnCust - The returnCust parameter value
   * @returns {Object|null} - Parsed express return data or null if invalid
   */
  _parseExpressReturn(returnCust) {
    const parts = returnCust.split('_');

    // Must have 7 parts (SECONDARY) or 8 parts (PRIMARY with profileType)
    if (parts.length !== 7 && parts.length !== 8) {
      return null;
    }

    const [prefix1, prefix2, prefix3, version, appId, templateId, applicantType, profileType] = parts;

    // Validate prefix parts
    if (prefix1 !== 'COAF' || prefix2 !== 'REFI' || prefix3 !== 'ER') {
      return null;
    }

    // Validate version format (V1, V2, etc.)
    if (!version || !version.match(/^V\d+$/i)) {
      return null;
    }

    // Validate appId and templateId exist
    if (!appId || !templateId) {
      return null;
    }

    // Validate applicantType
    if (applicantType !== 'PRIMARY' && applicantType !== 'SECONDARY') {
      return null;
    }

    // SECONDARY must not have profileType; PRIMARY must have a valid profileType
    if (applicantType === 'SECONDARY' && profileType !== undefined) {
      return null;
    }
    if (applicantType === 'PRIMARY' && !['FA', 'NFA', 'NA'].includes(profileType)) {
      return null;
    }

    return {
      type: 'express-return',
      version,
      appId,
      templateId,
      applicantType,
      profileType: profileType ?? null,
      isValidFormat: true,
      isCodelessEntryV1: false,
      isExpressReturn: true,
    };
  }

  /**
   * Validates the format without parsing (lightweight check)
   * @param {string} returnCust - The returnCust parameter value
   * @returns {boolean} - True if format is valid
   */
  isValidFormat(returnCust) {
    if (!returnCust || typeof returnCust !== 'string') {
      return false;
    }

    return this._isValidCodelessEntryFormat(returnCust) || this._isValidExpressReturnFormat(returnCust);
  }

  /**
   * Validates codeless entry format without parsing
   * @param {string} returnCust - The returnCust parameter value
   * @returns {boolean} - True if valid codeless entry format
   */
  _isValidCodelessEntryFormat(returnCust) {
    const parts = returnCust.split('_');
    return parts.length === 5 && parts[0] === 'COAF' && parts[1] === 'REFI' && parts[2] === 'CE' && /^V\d+$/i.test(parts[3]) && parts[4].length > 0;
  }

  /**
   * Validates express return format without parsing
   * @param {string} returnCust - The returnCust parameter value
   * @returns {boolean} - True if valid express return format
   */
  _isValidExpressReturnFormat(returnCust) {
    const parts = returnCust.split('_');
    if ((parts.length !== 7 && parts.length !== 8) || parts[0] !== 'COAF' || parts[1] !== 'REFI' || parts[2] !== 'ER' || !/^V\d+$/i.test(parts[3]) || !parts[4] || !parts[5]) {
      return false;
    }
    const applicantType = parts[6];
    const profileType = parts[7];
    if (applicantType === 'SECONDARY') return profileType === undefined;
    if (applicantType === 'PRIMARY') return ['FA', 'NFA', 'NA'].includes(profileType);
    return false;
  }

  /**
   * Validates allocation format for codeless entry
   * @param {string} allocation - The allocation string
   * @returns {boolean} - True if valid allocation format
   */
  isValidAllocation(allocation) {
    if (!allocation) {
      return false;
    }

    // Accept grlpv1 and other alphanumeric allocations
    return /^[a-zA-Z0-9]+$/.test(allocation);
  }

  /**
   * Validates app ID format (alphanumeric for express return)
   * @param {string} appId - The app ID string
   * @returns {boolean} - True if valid app ID format
   */
  isValidAppId(appId) {
    if (!appId) {
      return false;
    }

    // Accept alphanumeric app IDs
    return /^[a-zA-Z0-9]+$/.test(appId);
  }

  /**
   * Validates template ID format (alphanumeric for express return)
   * @param {string} templateId - The template ID string
   * @returns {boolean} - True if valid template ID format
   */
  isValidTemplateId(templateId) {
    if (!templateId) {
      return false;
    }

    // Accept alphanumeric template IDs
    return /^[a-zA-Z0-9]+$/.test(templateId);
  }

  /**
   * Validates version format (V1, V2, etc.)
   * @param {string} version - The version string
   * @returns {boolean} - True if valid version format
   */
  isValidVersion(version) {
    if (!version) {
      return false;
    }

    return /^V\d+$/i.test(version);
  }
}

const ReturnCustValidationService = new _ReturnCustValidationService();
export default ReturnCustValidationService;
